import { z } from 'zod'
import {
  CUSTOMER_RATING,
  LATE_PAYMENT_SEVERITY,
  PAYMENT_PROMPTNESS,
  type CustomerRating,
  type LatePaymentSeverity,
  type PaymentPromptness,
  type SubmitTradeReferencePayload,
  type TradeRefereePrefill,
} from '@/lib/api-types'
import { zodFieldErrors, type FieldErrors } from '@/lib/zod-errors'

/**
 * Client-side mirror of the API's public trade-reference zod schema
 * (greenline-hub-api routes/public/trade-reference.ts) — the form validates
 * against it before sending, so what clears here clears the server.
 */

export const refereeSchema = z.object({
  name: z.string().trim().min(1).max(120),
  title: z.string().trim().min(1).max(120),
  company: z.string().trim().min(1).max(255),
  phone: z.string().trim().max(40).optional(),
  address: z.string().trim().max(500).optional(),
  email: z.email().max(200),
})

export const responseSchema = z.object({
  referee: refereeSchema,
  accountLength: z.string().trim().min(1).max(100),
  averageMonthlyTurnover: z.number().nonnegative().max(1_000_000_000).optional(),
  creditTerms: z.string().trim().min(1).max(100),
  paymentPromptness: z.enum(PAYMENT_PROMPTNESS),
  latePaymentSeverity: z.enum(LATE_PAYMENT_SEVERITY),
  customerRating: z.enum(CUSTOMER_RATING),
  comments: z.string().trim().max(4000).optional(),
})

// ---------------------------------------------------------------------------
// Form state (string-typed inputs) → API payload
// ---------------------------------------------------------------------------

export interface RefereeFormState {
  name: string
  title: string
  company: string
  phone: string
  address: string
  email: string
}

export interface AnswersFormState {
  accountLength: string
  averageMonthlyTurnover: string
  creditTerms: string
  paymentPromptness: string
  latePaymentSeverity: string
  customerRating: string
  comments: string
}

export function initialReferee(prefill: TradeRefereePrefill): RefereeFormState {
  return {
    name: prefill.name ?? '',
    title: '',
    company: prefill.company ?? '',
    phone: prefill.phone ?? '',
    address: '',
    email: prefill.email ?? '',
  }
}

export function emptyAnswers(): AnswersFormState {
  return {
    accountLength: '',
    averageMonthlyTurnover: '',
    creditTerms: '',
    paymentPromptness: '',
    latePaymentSeverity: '',
    customerRating: '',
    comments: '',
  }
}

function opt(value: string): string | undefined {
  const trimmed = value.trim()
  return trimmed === '' ? undefined : trimmed
}

/** The exact POST body — undefined optionals drop out of the JSON entirely. */
export function buildReferencePayload(
  referee: RefereeFormState,
  answers: AnswersFormState,
): SubmitTradeReferencePayload {
  const turnover = answers.averageMonthlyTurnover.trim()
  return {
    referee: {
      name: referee.name.trim(),
      title: referee.title.trim(),
      company: referee.company.trim(),
      phone: opt(referee.phone),
      address: opt(referee.address),
      email: referee.email.trim(),
    },
    accountLength: answers.accountLength.trim(),
    averageMonthlyTurnover: turnover === '' ? undefined : Number.parseFloat(turnover),
    creditTerms: answers.creditTerms.trim(),
    paymentPromptness: answers.paymentPromptness as PaymentPromptness,
    latePaymentSeverity: answers.latePaymentSeverity as LatePaymentSeverity,
    customerRating: answers.customerRating as CustomerRating,
    comments: opt(answers.comments),
  }
}

/** Keys match the form's field ids (`referee.name`, `paymentPromptness`, …). */
export function validateReference(
  referee: RefereeFormState,
  answers: AnswersFormState,
): FieldErrors {
  return zodFieldErrors(responseSchema.safeParse(buildReferencePayload(referee, answers)))
}
